/**
 * Credit Purchase Hook
 *
 * Provides real credit balance data and a purchase function.
 *
 * Card on file: charges immediately via Stripe Invoice API.
 * No card: returns { success: false, needsPaymentMethod: true } so the
 *   caller can prompt the user to add a payment method first.
 *
 * Preview mode: delegates to CreditsContext for local state behavior.
 * Production mode: uses useBilling() for real data + purchaseCreditPack mutation.
 */

import { useCallback, useMemo } from 'react';
import { isLovablePreview } from '@/lib/utils/environment';
import { useBilling, type BillingInfo } from './use-billing';
import { useWorkspaceId } from './use-workspace-id';
import { usePurchaseCreditPackMutation, CreditSourceKind } from '@/lib/graphql/types-and-hooks';

/**
 * Net credits available across all sources (subscription + non-expired packs).
 * Negative = genuine overage.
 *
 * Summed per source as `totalCredits - usedCredits`, NOT
 * `totalCreditsLimit - totalCreditsUsed`: the latter under-counts because
 * `totalCreditsLimit` only includes a pack's *remaining* credits (0 once spent)
 * while `totalCreditsUsed` still includes everything consumed — so a
 * fully-used pack looked like a large overage and falsely tripped the
 * overage gate / "0 balance" on load.
 */
export function netCreditsAvailable(billingInfo: BillingInfo | null | undefined): number {
  const sources = billingInfo?.creditSources;
  if (sources && sources.length > 0) {
    return sources.reduce((n, s) => n + ((s.totalCredits ?? 0) - (s.usedCredits ?? 0)), 0);
  }
  // Fallback when per-source data is absent.
  return (billingInfo?.totalCreditsLimit ?? 0) - (billingInfo?.totalCreditsUsed ?? 0);
}

const LOW_CREDIT_THRESHOLD = 0.15; // warn at 15% remaining

export interface CreditPurchaseInfo {
  /** Credits remaining (limit - used) */
  balance: number;
  /** Total credit limit (subscription + purchased) */
  limit: number;
  /** Credits from purchased packs */
  purchasedRemaining: number;
  /** Whether credits are at or below 15% of limit */
  isLowCredits: boolean;
  /** Check if user has enough credits for an action */
  hasEnoughCredits: (amount: number) => boolean;
  /**
   * Purchase a credit pack.
   *
   * Card on file: charges immediately, returns { success: true }.
   * No card: redirects to Stripe Checkout (same tab) to collect payment.
   */
  purchaseCreditPack: (packId: string) => Promise<{
    success: boolean;
    invoiceId?: string | null;
  }>;
  /** Whether the purchase mutation is in flight */
  purchasing: boolean;
  /** Whether billing data is loading */
  loading: boolean;
}

export function useCreditPurchase(): CreditPurchaseInfo {
  const preview = isLovablePreview();
  const { billingInfo, loading } = useBilling();
  const workspaceId = useWorkspaceId();

  const [purchaseMutation, { loading: purchasing }] = usePurchaseCreditPackMutation();

  const balance = useMemo(() => Math.max(0, netCreditsAvailable(billingInfo)), [billingInfo]);

  const limit = billingInfo?.totalCreditsLimit ?? 0;
  const purchasedRemaining = useMemo(
    () =>
      (billingInfo?.creditSources ?? [])
        .filter((s) => s.kind === CreditSourceKind.Pack)
        .reduce((sum, s) => sum + (s.remainingCredits ?? 0), 0),
    [billingInfo],
  );

  const isLowCredits = useMemo(() => {
    if (limit <= 0) return false;
    return balance / limit <= LOW_CREDIT_THRESHOLD;
  }, [balance, limit]);

  const hasEnoughCredits = useCallback(
    (amount: number) => balance >= amount,
    [balance],
  );

  const purchaseCreditPack = useCallback(
    async (packId: string) => {
      if (preview || !workspaceId) {
        return { success: false };
      }

      // Build a return URL pointing back to the current page with purchased=1
      const returnUrl = new URL(window.location.href);
      returnUrl.searchParams.set('purchased', '1');

      const { data } = await purchaseMutation({
        variables: {
          input: {
            workspaceId,
            packId,
            successUrl: returnUrl.toString(),
          },
        },
        refetchQueries: ['GetCreditUsageSummary'],
      });

      const result = data?.purchaseCreditPack;
      if (!result) {
        throw new Error('Purchase failed — no response from server');
      }

      if (result.success) {
        return { success: true, invoiceId: result.invoiceId };
      }

      // No card on file → redirect to Stripe Checkout (same tab)
      // Stripe redirects back directly to the current page (with purchased=1)
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return new Promise<{ success: boolean; invoiceId?: string }>(() => {});
      }

      throw new Error('Unexpected response from purchase mutation');
    },
    [preview, workspaceId, purchaseMutation],
  );

  return {
    balance,
    limit,
    purchasedRemaining,
    isLowCredits,
    hasEnoughCredits,
    purchaseCreditPack,
    purchasing,
    loading,
  };
}
